import {
    BaseInput,
    BaseSelect,
    CurrencyType,
    formatDate,
    ISelectOption,
    ProviderPatientStatement,
    IToastContextProps,
} from '@enter/core';
import styled from '@emotion/styled';
import React, { useState } from 'react';
import { IClaimStore, IDialogContext, IStatementData } from '../statement-details.types';
import { isRefundAllocation } from '../statement-details.data';
import { formatMoney, parseMoney } from '../utils/statement-format.utils';
import * as S from './invoice-item-dialog.styles';
import { ErrorText } from './statement-ledger-dialog.styles';

const FormSection = styled.div`
    margin-top: 16px;
`;

type EditPaymentDialogProps = {
    patientStatement?: ProviderPatientStatement;
    selectedRow?: IStatementData;
    /** Posted payments for this source. More than one shows a picker so each can be moved. */
    editRows?: IStatementData[];
    mode?: 'edit' | 'allocate';
    onClose: () => void;
    onSuccess: () => void;
    claimStore: IClaimStore;
    dialogContext: IDialogContext;
    toastContext: IToastContextProps;
};

const errorMessage = (err: unknown): string =>
    err instanceof Error && err.message ? err.message : 'Something went wrong. Please try again.';

const postedPaymentLabel = (row: IStatementData): string => {
    const claim = row.description || 'Claim';
    const posted = formatMoney(parseMoney(row.credit?.amount));
    return `${claim} · ${posted}`;
};

export const EditPaymentDialog: React.FunctionComponent<EditPaymentDialogProps> = props => {
    const { claimStore, dialogContext, toastContext, patientStatement, selectedRow, editRows, onClose, onSuccess } =
        props;
    const mode = props.mode || 'edit';
    const isAllocate = mode === 'allocate';
    const isRefund = isAllocate && isRefundAllocation(selectedRow);
    const postedRows = !isAllocate && editRows?.length ? editRows : [];
    const [postedPaymentId, setPostedPaymentId] = useState<string>(postedRows[0]?.id || '');
    const postedRow = postedRows.find(row => row.id === postedPaymentId) || postedRows[0];
    const activeRow = isAllocate ? selectedRow : postedRow || selectedRow;
    // Allocate rows carry the unallocated remainder as `credit` (negative for an unapplied refund).
    // The form takes a positive amount; the sign is applied on submit.
    const maxAllocatable = isAllocate ? Math.abs(parseMoney(selectedRow?.credit?.amount)) : undefined;

    const [amount, setAmount] = useState<string>(
        maxAllocatable !== undefined ? maxAllocatable.toFixed(2) : activeRow?.credit?.amount || '',
    );
    // In allocate mode `selectedRow.claimId` is the payment SOURCE id, not a claim — never prefill it.
    const [claimId, setClaimId] = useState<string>(isAllocate ? '' : activeRow?.claimId || '');
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    const claimOptions: ISelectOption[] = React.useMemo(() => {
        if (!patientStatement?.claims) return [];
        return patientStatement.claims.map(claim => ({
            label: `${claim.externalId} - ${claim.fromDos ? formatDate(claim.fromDos, 'MMM dd, yyyy') : ''}`,
            value: claim.id,
        }));
    }, [patientStatement]);

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};
        const parsedAmount = parseMoney(amount);

        // Editing may reverse or zero a posted payment; allocation takes an unsigned magnitude.
        if (!amount.trim() || !Number.isFinite(Number(amount))) {
            newErrors.amount = 'Amount is required and must be a valid number';
        } else if (isAllocate && parsedAmount < 0) {
            newErrors.amount = 'Allocation amount cannot be negative';
        } else if (maxAllocatable !== undefined && parsedAmount > maxAllocatable + 0.005) {
            newErrors.amount = `Amount cannot exceed the unallocated ${formatMoney(maxAllocatable)}`;
        }

        if (!claimId) {
            newErrors.claimId = 'Claim is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (): Promise<void> => {
        if (!validate()) {
            return;
        }

        setIsSubmitting(true);

        try {
            if (isAllocate) {
                const collectionPaymentSourceId = selectedRow?.claimId;
                if (!collectionPaymentSourceId) {
                    throw new Error('Collection payment source ID is missing');
                }
                if (!claimStore.allocateCollectionPaymentSource) {
                    throw new Error('Allocate payment is not supported in this context');
                }
                const signedAmount = isRefund ? -parseMoney(amount) : parseMoney(amount);
                await claimStore.allocateCollectionPaymentSource(
                    collectionPaymentSourceId,
                    {
                        amount: signedAmount.toFixed(2),
                        currency: CurrencyType.USD,
                    },
                    claimId,
                );
                toastContext.notify?.(
                    isRefund ? 'Refund was applied successfully.' : 'Payment was allocated successfully.',
                    'right',
                );
            } else {
                const paymentId = activeRow?.metadata?.id;
                if (!paymentId) {
                    throw new Error('Payment ID is missing');
                }
                await claimStore.updateCollectionPayment(paymentId, {
                    claimId,
                    amount: {
                        amount: amount,
                        currency: CurrencyType.USD,
                    },
                });
                toastContext.notify?.('Payment was updated successfully.', 'right');
            }
            onSuccess();
        } catch (err) {
            // Shown inline too: the support app's dialogContext.showError is a no-op.
            setErrors(prev => ({ ...prev, form: errorMessage(err) }));
            dialogContext.showError(err as Error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = (): void => {
        onClose();
    };

    const handleClaimChange = (option: any): void => {
        setClaimId((option?.value as string) || '');
    };

    const handlePostedPaymentChange = (option: any): void => {
        const nextId = (option?.value as string) || '';
        const next = postedRows.find(row => row.id === nextId);
        if (!next) {
            return;
        }
        setPostedPaymentId(next.id);
        setClaimId(next.claimId || '');
        setAmount(next.credit?.amount || '');
        setErrors({});
    };

    const postedOptions: ISelectOption[] = postedRows.map(row => ({
        label: postedPaymentLabel(row),
        value: row.id,
    }));

    const savingLabel = isAllocate ? (isRefund ? 'Applying...' : 'Allocating...') : 'Updating...';
    const saveLabel = isAllocate ? (isRefund ? 'Apply refund' : 'Allocate') : 'Update';

    return (
        <S.DialogBody>
            {postedOptions.length > 1 && (
                <FormSection>
                    <S.Label>Posted payment</S.Label>
                    <BaseSelect
                        options={postedOptions}
                        value={postedOptions.find(opt => opt.value === postedPaymentId)}
                        onChange={handlePostedPaymentChange}
                        placeholder="Select a posted payment"
                        required
                    />
                </FormSection>
            )}
            <FormSection>
                <S.Label>{isRefund ? 'Claim To Apply Refund To' : 'Claim To Post Payment To'}</S.Label>
                <BaseSelect
                    options={claimOptions}
                    value={claimOptions.find(opt => opt.value === claimId)}
                    onChange={handleClaimChange}
                    errorMessage={errors.claimId}
                    placeholder="Select a claim"
                    required
                />
            </FormSection>

            <FormSection>
                <S.Label>
                    {isRefund ? 'Refund amount' : 'Amount'}
                    {maxAllocatable !== undefined && ` (unallocated ${formatMoney(maxAllocatable)})`}
                </S.Label>
                <BaseInput
                    type="number"
                    value={amount}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAmount(e.target.value)}
                    errorMessage={errors.amount}
                    placeholder="0.00"
                />
            </FormSection>

            {errors.form && <ErrorText>{errors.form}</ErrorText>}

            <S.ButtonGroup
                changed={true}
                useNotify={false}
                onCancel={handleCancel}
                onSave={handleSubmit}
                saveLabel={isSubmitting ? savingLabel : saveLabel}
                saveNotice={
                    isAllocate
                        ? isRefund
                            ? 'Refund applied successfully'
                            : 'Payment allocated successfully'
                        : 'Payment updated successfully'
                }
            />
        </S.DialogBody>
    );
};
